// Personas.js
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { FaUser, FaPlus, FaEdit, FaTrash, FaLinkedin } from "react-icons/fa";
import { FaUserTie } from "react-icons/fa";
import { toast } from "react-toastify";
import PersonaModal from "./components/PersonaModal";

/**
 * Personas Component
 *
 * This component displays and manages sender personas in a grid view.
 * It shares the same data source as the PersonaSelector component used in the Campaign Builder.
 * Personas created or managed here will be available for selection in campaigns.
 *
 * The navigation between Personas and Campaign Builder is coordinated through the Sidebar.
 */
const Personas = () => {
  const { user } = useContext(AuthContext);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);

  useEffect(() => {
    const fetchPersonas = async () => {
      if (!user) return;

      try {
        // Fix the collection path - add "collections" segment to make path have odd number of segments
        const personasQuery = query(
          collection(
            db,
            "userContent",
            user.uid,
            "aiSdr",
            "collections", // Add this segment to make path have odd number of segments
            "personas"
          ),
          orderBy("createdAt", "desc")
        );

        const personasSnapshot = await getDocs(personasQuery);

        const personasList = personasSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        }));

        setPersonas(personasList);
      } catch (error) {
        console.error("Error fetching personas:", error);
        toast.error("Failed to load personas");
      } finally {
        setLoading(false);
      }
    };

    fetchPersonas();
  }, [user]);

  const deletePersona = async (personaId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this persona? This action cannot be undone."
      )
    ) {
      return;
    }

    if (!user) return;

    try {
      // Fix the document path by adding "collections" segment
      const personaRef = doc(
        db,
        "userContent",
        user.uid,
        "aiSdr",
        "collections", // Add this segment to make path structure consistent
        "personas",
        personaId
      );
      await deleteDoc(personaRef);

      setPersonas((prev) => prev.filter((persona) => persona.id !== personaId));
      toast.success("Persona deleted");
    } catch (error) {
      console.error("Error deleting persona:", error);
      toast.error("Failed to delete persona");
    }
  };

  const handleCreatePersona = () => {
    setShowModal(true);
    setSelectedPersona(null);
  };

  const handleEditPersona = (persona) => {
    setShowModal(true);
    setSelectedPersona(persona);
  };

  const handleSavePersona = (persona) => {
    // Implementation of handleSavePersona
    console.log("Saving persona:", persona);
    setShowModal(false);
    setSelectedPersona(null);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Seller Personas</h1>
          <button
            onClick={() => handleCreatePersona()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
          >
            <FaPlus className="mr-2" /> Create Persona
          </button>
        </div>

        {/* Personas Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            // Loading state
            Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 animate-pulse"
              >
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-10 bg-gray-200 rounded mt-6"></div>
              </div>
            ))
          ) : personas.length > 0 ? (
            personas.map((persona) => (
              <div
                key={persona.id}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div
                  className="h-3"
                  style={{
                    backgroundColor: persona.color || "#6D28D9",
                  }}
                ></div>
                <div className="p-6">
                  <h2 className="text-xl font-semibold mb-2">{persona.name}</h2>
                  <div className="mb-4">
                    <p className="text-gray-600 text-sm">
                      {persona.role} at {persona.company}
                    </p>
                    <p className="text-gray-600 text-sm">{persona.email}</p>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">
                      Tone & Style:
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {persona.tone || "Professional"}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">
                      Selling Points:
                    </h3>
                    <p className="text-gray-600 text-sm truncate">
                      {persona.sellingPoints || "No selling points specified"}
                    </p>
                  </div>

                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => handleEditPersona(persona)}
                      className="text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <FaEdit className="mr-1" /> Edit
                    </button>
                    <button
                      onClick={() => deletePersona(persona.id)}
                      className="text-red-600 hover:text-red-800 flex items-center"
                    >
                      <FaTrash className="mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center bg-white rounded-lg shadow-md p-8">
              <FaUserTie className="text-purple-400 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-800 mb-2">
                No personas yet
              </h3>
              <p className="text-gray-600 mb-6 text-center max-w-md">
                Create a persona to represent yourself or your sales team. This
                information will be used to personalize email templates.
              </p>
              <button
                onClick={() => handleCreatePersona()}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <FaPlus className="mr-2" /> Create Your First Persona
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Persona Modal */}
      {showModal && (
        <PersonaModal
          persona={selectedPersona}
          onClose={() => {
            setShowModal(false);
            setSelectedPersona(null);
          }}
          onSave={handleSavePersona}
        />
      )}
    </div>
  );
};

export default Personas;
